import type { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { Cart, useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type { UpdateCartBuyerIdentityMutation } from 'services/shopify';
import { ShopifyService } from 'services/shopify';
import { convertToCountryCodeEnum } from 'utils/convertToCountryCodeEnum';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

import { CART_KEY } from './useCart';

export async function updateCartBuyerIdentity(
  country: string,
  cartId?: string,
): Promise<
  NonNullable<
    UpdateCartBuyerIdentityMutation['cartBuyerIdentityUpdate']
  >['cart']
> {
  if (cartId) {
    const { cartBuyerIdentityUpdate } =
      await ShopifyService.updateCartBuyerIdentity({
        cartId,
        buyerIdentity: {
          countryCode: convertToCountryCodeEnum(country),
        },
      });

    const cart = cartBuyerIdentityUpdate?.cart;

    if (cart) {
      return cart;
    }
  }
}

export const useUpdateCartBuyerIdentity = (queryClient: QueryClient) => {
  const {
    value: { countryCode },
  } = useLocalStorage(UserLocale);
  const {
    value: { id: cartId },
  } = useLocalStorage(Cart);

  return useMutation({
    mutationFn: (country: string) => updateCartBuyerIdentity(country, cartId),
    onSuccess: (cart) => {
      if (cart) {
        queryClient.setQueriesData(
          { queryKey: [CART_KEY, countryCode] },
          mapShopifyCart(cart),
        );
      }
    },
  });
};
